import { useAuth0 } from '@auth0/auth0-react'
import { Activity, ArrowRight, Shield, Users, Calendar, BarChart3 } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

const features = [
  {
    icon: Users,
    title: 'Client Management',
    description: 'Efficiently manage your assigned clients with progress tracking and communication tools.'
  },
  {
    icon: Calendar,
    title: 'Schedule & Sessions',
    description: 'Organize your coaching sessions and manage your availability with ease.'
  },
  {
    icon: BarChart3,
    title: 'Progress Analytics',
    description: 'Track client progress with detailed analytics and performance metrics.'
  },
  {
    icon: Shield,
    title: 'Secure & Professional',
    description: 'HIPAA-compliant platform designed specifically for fitness professionals.'
  }
]

export function LoginPage() {
  const { loginWithRedirect, isLoading } = useAuth0()

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: '/dashboard'
      }
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left side - Branding and features */}
        <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto lg:max-w-lg">
            {/* Logo and title */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl">
                  <Activity className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Fit Coach Web
              </h1>
              <p className="text-lg text-gray-600">
                Professional coaching platform designed for individual fitness coaches
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => {
                const Icon = feature.icon
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-lg">
                        <Icon className="w-4 h-4 text-primary-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Trust indicators */}
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Trusted by professional coaches worldwide
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Login form */}
        <div className="flex-1 flex flex-col justify-center bg-white px-4 sm:px-6 lg:px-8 py-12 lg:shadow-xl">
          <div className="max-w-sm mx-auto w-full">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome Back
              </h2>
              <p className="text-gray-600">
                Sign in to access your coaching dashboard
              </p>
            </div>

            {/* Login button */}
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" color="white" />
              ) : (
                <>
                  <span>Sign in with Auth0</span>
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </button>

            {/* Security note */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Secure authentication powered by Auth0
              </p>
            </div>

            {/* Additional info */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">
                  New to Fit Coach?
                </p>
                <p className="text-xs text-gray-500">
                  Contact your administrator for account setup
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}